import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    Grid,
    TextField,
    Typography,
    IconButton,
    MenuItem,
    Divider,
    LinearProgress,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FaPaperclip } from "react-icons/fa6";
import { FiUpload } from "react-icons/fi";
import { FiChevronDown } from "react-icons/fi";
import { LuTrash2 } from "react-icons/lu";
import { LuDownload } from "react-icons/lu";
import { IoAdd } from "react-icons/io5";
import { LuX } from "react-icons/lu";
import CalenderSVG from "assets/logo/Calendar";
import { useDispatch } from "store";
import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import moment from "moment/moment";

const EditSafety = (props) => {
    const dispatch = useDispatch();
    const { open, handleClose, rowData } = props;

    const [workflow, setWorkflow] = useState("");
    const [contractor, setContractor] = useState("");
    const [files, setFiles] = useState([]);

    const validationSchema = Yup.object({
        labourHours: Yup.number()
            .typeError("Please enter a valid number")
            .required("Labour Man Hours is required"),
        manHoursSinceLTI: Yup.number()
            .typeError("Please enter a valid number")
            .required("Man hours since last LTA LTI is required"),
        nearMiss: Yup.number()
            .typeError("Please enter a valid number")
            .required("Near Miss Incident is required"),
        ltaLti: Yup.number()
            .typeError("Please enter a valid number")
            .required("LTAs LTIs is required"),
        fatalities: Yup.number()
            .typeError("Please enter a valid number")
            .required("Fatalities is required"),
        date: Yup.date().required("Date is required"),
        workflow: Yup.string().required("Workflow is required"),
        contractor: Yup.string().required("Consultant/Contractor is required"),
    });

    const formik = useFormik({
        initialValues: {
            labourHours: rowData?.labourHours,
            manHoursSinceLTI: rowData?.manHoursSinceLTI,
            nearMiss: rowData?.nearMiss,
            ltaLti: rowData?.ltaLti,
            fatalities: rowData?.fatalities,
            date: moment(rowData?.date)?.format("YYYY-MM-DD"),
            workflow: rowData?.workflow,
            contractor: rowData?.contractor,
        },
        validationSchema,
        onSubmit: (values) => {
            openNotification("Safety data updated successfully!", 'success')
            handleClose();
        },
    });

    useEffect(() => {
        if (open) {
            formik.resetForm();
        }
    }, [open]);

    const handleFileUpload = (e) => {

        const now = new Date();
        const day = now.toLocaleString("en-GB", { day: "2-digit" });
        const month = now.toLocaleString("en-GB", { month: "short" });
        const year = now.getFullYear();
        const formattedDate = `${day} ${month}, ${year}`;

        const uploadedFiles = Array.from(e.target.files).map((file) => ({
            id: Date.now() + Math.random(),
            name: file.name,
            size: (file.size / 1024).toFixed(1) + " KB",
            date: formattedDate,
            type: file.name.split(".").pop().toUpperCase(),
            progress: 0,
            status: "uploading",
            error:
                file.size > 2 * 1024 * 1024
                    ? "The file is too large to upload."
                    : null,
        }));

        setFiles((prev) => [...prev, ...uploadedFiles]);

        uploadedFiles.forEach((file) => {
            if (file.error) return;

            const uploadInterval = setInterval(() => {
                setFiles((prevFiles) =>
                    prevFiles.map((f) =>
                        f.id === file.id
                            ? {
                                ...f,
                                progress: Math.min(f.progress + Math.floor(Math.random() * 15), 100),
                                status:
                                    f.progress + 15 >= 100 ? "uploaded" : "uploading",
                            }
                            : f
                    )
                );
            }, 300);

            // Stop each upload once done
            setTimeout(() => clearInterval(uploadInterval), 4000);
        });
    };

    const handleRemoveFile = (id) => {
        setFiles((prev) => prev.filter((f) => f.id !== id));
    };

    const labelTextStyle = {
        fontWeight: 500,
        fontSize: "16px",
        lineHeight: "100%",
        color: "#5F5F5F",
        fontFamily: "Outfit, sans-serif",
        mb: 1,
        mt: 3,
    };

    const textStyle = {
        "& .MuiInputBase-input": {
            fontWeight: 400,
            fontSize: "16px",
            lineHeight: "100%",
            letterSpacing: "0.16px",
            color: "#0A0A0C",
            fontFamily: "Outfit, sans-serif",
        },
    };

    const rightLabelStyle = {
        fontWeight: 500,
        fontSize: "16px",
        lineHeight: "100%",
        color: "#5F5F5F",
        fontFamily: "Outfit, sans-serif",
    };

    const rightTextStyle = {
        "& .MuiInputBase-input": {
            fontWeight: 400,
            fontSize: "16px",
            lineHeight: "100%",
            letterSpacing: "0.16px",
            color: "#0A0A0C",
            fontFamily: "Outfit, sans-serif",
        },
    };

    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    return (
        <>
            {/* ---------- Popup Dialog ---------- */}
            <Dialog
                open={open}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: { borderTopLeftRadius: "5px", borderTopRightRadius: "5px", backgroundColor: "#FFFFFF", width: "100%", overflowX: "hidden" },
                }}
                disableEscapeKeyDown
                onClose={(event, reason) => {
                    if (reason !== "backdropClick") {
                        handleClose();
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: "1px solid #ddd",
                        backgroundColor: "#ECECEC",
                        pb: 2,
                        pt: 3,
                        width: "100%",
                    }}
                >
                    <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: "27px", lineHeight: "100%", color: "#140F36", fontFamily: "Outfit, sans-serif" }}>
                            Edit Safety
                        </Typography>
                        <Typography sx={{
                            pt: 2, fontWeight: 400, fontSize: "14px", lineHeight: "100%", color: "#433F5E", fontFamily: "Outfit, sans-serif",
                        }}>
                            Edit safety form for your project
                        </Typography>
                    </Box>

                </DialogTitle>

                <form onSubmit={formik.handleSubmit}>
                    <DialogContent sx={{ width: "100%", pt: 1, pb: 4, overflowX: "hidden", boxSizing: "border-box" }}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", md: "row" },
                                gap: { xs: 2, md: 5 },
                                width: "100%",
                                boxSizing: "border-box",
                                overflowX: "hidden",
                            }}
                        >
                            {/* Left Side Inputs */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={labelTextStyle}>
                                    Labour Man Hours
                                </Typography>
                                <TextField
                                    fullWidth
                                    name="labourHours"
                                    value={formik.values.labourHours}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}

                                    placeholder="Enter Man Hours in numbers"
                                    InputProps={{
                                        sx: {
                                            height: "50px",
                                            borderRadius: "8px",
                                            backgroundColor: "#fff",

                                            "& input::placeholder": {
                                                fontWeight: 400,
                                                fontSize: "16px",
                                                lineHeight: "100%",
                                                letterSpacing: "0.16px",
                                                color: "#0A0A0C",
                                                fontFamily: "Outfit, sans-serif",
                                                opacity: 1,
                                            },

                                            "& input": {
                                                fontWeight: 400,
                                                fontSize: "16px",
                                                lineHeight: "100%",
                                                letterSpacing: "0.16px",
                                                color: "#0A0A0C",
                                                fontFamily: "Outfit, sans-serif",
                                            },
                                        },
                                    }}
                                />
                                {formik.touched.labourHours && formik.errors.labourHours && (
                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                        {formik.errors.labourHours}
                                    </Typography>
                                )}

                                <Typography sx={labelTextStyle}>
                                    Man hours since last LTA LTI
                                </Typography>
                                <TextField
                                    fullWidth
                                    name="manHoursSinceLTI"
                                    value={formik.values.manHoursSinceLTI}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="Enter Man Hours since last LTA LTI in numbers"
                                    InputProps={{
                                        sx: {
                                            height: "50px",
                                            borderRadius: "8px",
                                            backgroundColor: "#fff",

                                            "& input::placeholder": {
                                                fontWeight: 400,
                                                fontSize: "16px",
                                                lineHeight: "100%",
                                                letterSpacing: "0.16px",
                                                color: "#0A0A0C",
                                                fontFamily: "Outfit, sans-serif",
                                                opacity: 1,
                                            },

                                            "& input": {
                                                fontWeight: 400,
                                                fontSize: "16px",
                                                lineHeight: "100%",
                                                letterSpacing: "0.16px",
                                                color: "#0A0A0C",
                                                fontFamily: "Outfit, sans-serif",
                                            },
                                        },
                                    }}
                                />
                                {formik.touched.manHoursSinceLTI && formik.errors.manHoursSinceLTI && (
                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                        {formik.errors.manHoursSinceLTI}
                                    </Typography>
                                )}

                                <Typography sx={labelTextStyle}>
                                    Near Miss Incident
                                </Typography>
                                <TextField
                                    fullWidth
                                    name="nearMiss"
                                    value={formik.values.nearMiss}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}

                                    placeholder="Enter Near miss incidents in numbers"
                                    InputProps={{
                                        sx: {
                                            height: "50px",
                                            borderRadius: "8px",
                                            backgroundColor: "#fff",

                                            "& input::placeholder": {
                                                fontWeight: 400,
                                                fontSize: "16px",
                                                lineHeight: "100%",
                                                letterSpacing: "0.16px",
                                                color: "#0A0A0C",
                                                fontFamily: "Outfit, sans-serif",
                                                opacity: 1,
                                            },

                                            "& input": {
                                                fontWeight: 400,
                                                fontSize: "16px",
                                                lineHeight: "100%",
                                                letterSpacing: "0.16px",
                                                color: "#0A0A0C",
                                                fontFamily: "Outfit, sans-serif",
                                            },
                                        },
                                    }}
                                />
                                {formik.touched.nearMiss && formik.errors.nearMiss && (
                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                        {formik.errors.nearMiss}
                                    </Typography>
                                )}

                                <Typography sx={labelTextStyle}>
                                    LTAs LTIs
                                </Typography>
                                <TextField
                                    fullWidth
                                    name="ltaLti"
                                    value={formik.values.ltaLti}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}

                                    placeholder="Enter LTAs LTIs in numbers"
                                    InputProps={{
                                        sx: {
                                            height: "50px",
                                            borderRadius: "8px",
                                            backgroundColor: "#fff",

                                            "& input::placeholder": {
                                                fontWeight: 400,
                                                fontSize: "16px",
                                                lineHeight: "100%",
                                                letterSpacing: "0.16px",
                                                color: "#0A0A0C",
                                                fontFamily: "Outfit, sans-serif",
                                                opacity: 1,
                                            },

                                            "& input": {
                                                fontWeight: 400,
                                                fontSize: "16px",
                                                lineHeight: "100%",
                                                letterSpacing: "0.16px",
                                                color: "#0A0A0C",
                                                fontFamily: "Outfit, sans-serif",
                                            },
                                        },
                                    }}
                                />
                                {formik.touched.ltaLti && formik.errors.ltaLti && (
                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                        {formik.errors.ltaLti}
                                    </Typography>
                                )}

                                <Typography sx={labelTextStyle}>
                                    Fatalities
                                </Typography>
                                <TextField
                                    fullWidth
                                    name="fatalities"
                                    value={formik.values.fatalities}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}

                                    placeholder="Enter fatalities in numbers"
                                    InputProps={{
                                        sx: {
                                            height: "50px",
                                            borderRadius: "8px",
                                            backgroundColor: "#fff",

                                            "& input::placeholder": {
                                                fontWeight: 400,
                                                fontSize: "16px",
                                                lineHeight: "100%",
                                                letterSpacing: "0.16px",
                                                color: "#0A0A0C",
                                                fontFamily: "Outfit, sans-serif",
                                                opacity: 1,
                                            },

                                            "& input": {
                                                fontWeight: 400,
                                                fontSize: "16px",
                                                lineHeight: "100%",
                                                letterSpacing: "0.16px",
                                                color: "#0A0A0C",
                                                fontFamily: "Outfit, sans-serif",
                                            },
                                        },
                                    }}
                                />
                                {formik.touched.fatalities && formik.errors.fatalities && (
                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                        {formik.errors.fatalities}
                                    </Typography>
                                )}
                            </Box>

                            {/* Right Side Inputs */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 3 }}>
                                    Date
                                </Typography>
                                <TextField
                                    type="date"
                                    name="date"
                                    value={formik.values.date}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}

                                    fullWidth
                                    sx={{
                                        ...rightTextStyle,
                                        "& input::-webkit-calendar-picker-indicator": {
                                            display: "none",
                                            WebkitAppearance: "none",
                                        },
                                        "& input[type='date']": {
                                            appearance: "none",
                                            WebkitAppearance: "none",
                                        },
                                        "& .MuiOutlinedInput-root": {
                                            "& fieldset": {
                                                border: "none",
                                            },
                                        },
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}
                                                onClick={(e) => {
                                                    // Trigger the native date picker
                                                    const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                    if (input) input.showPicker?.();
                                                }}
                                            >
                                                {CalenderSVG}
                                            </Box>
                                        ),
                                        sx: {
                                            backgroundColor: "#E7E7EB",
                                            height: "35px",
                                            //width: { xs: "100%", md: "45%" },
                                            width: "100%",
                                            borderRadius: "5px",
                                        },
                                    }}
                                />
                                {formik.touched.date && formik.errors.date && (
                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                        {formik.errors.date}
                                    </Typography>
                                )}

                                <Typography sx={{ ...rightLabelStyle, mt: 2, mb: 1 }}>
                                    Workflow
                                </Typography>
                                <TextField select fullWidth
                                    name="workflow"
                                    value={formik.values.workflow}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}

                                    SelectProps={{ displayEmpty: true, IconComponent: FiChevronDown }}
                                    sx={{
                                        ...rightTextStyle,
                                        "& .MuiOutlinedInput-root": {
                                            "& fieldset": {
                                                border: "none",
                                            },
                                            "& .MuiSelect-icon": {
                                                color: "#292D32",
                                                fontSize: "18px",
                                                right: "45px",
                                            },
                                        },
                                    }}
                                    InputProps={{
                                        sx: {
                                            backgroundColor: "#E7E7EB",
                                            height: "35px",
                                            //width: { xs: "100%", md: "50%" },
                                            width: "100%",
                                            borderRadius: "5px",
                                            pt: "5px",
                                        },
                                    }}
                                >
                                    <MenuItem value="" disabled>Select Workflow</MenuItem>
                                    <MenuItem value="daily">Daily Check</MenuItem>
                                    <MenuItem value="weekly">Weekly Inspection</MenuItem>
                                </TextField>
                                {formik.touched.workflow && formik.errors.workflow && (
                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                        {formik.errors.workflow}
                                    </Typography>
                                )}

                                <Typography sx={{ ...rightLabelStyle, mt: 2, mb: 1 }}>
                                    Consultant/Contractor
                                </Typography>
                                <TextField select fullWidth
                                    name="contractor"
                                    value={formik.values.contractor}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}

                                    SelectProps={{ displayEmpty: true, IconComponent: FiChevronDown }}
                                    sx={{
                                        ...rightTextStyle,
                                        "& .MuiOutlinedInput-root": {
                                            "& fieldset": {
                                                border: "none", // static border
                                            },
                                        },
                                        "& .MuiSelect-icon": {
                                            color: "#292D32",
                                            fontSize: "18px",
                                            right: "12px",
                                        },
                                    }}
                                    InputProps={{
                                        sx: {
                                            backgroundColor: "#E7E7EB",
                                            height: "35px",
                                            //width: { xs: "100%", md: "65%" },
                                            width: "100%",
                                            borderRadius: "5px",
                                            pt: "5px",
                                        },
                                    }}>
                                    <MenuItem value="" disabled>Select Contractor/Consultant</MenuItem>
                                    <MenuItem value="ABC">ABC Constructions</MenuItem>
                                    <MenuItem value="XYZ">XYZ Safety Ltd</MenuItem>
                                </TextField>
                                {formik.touched.contractor && formik.errors.contractor && (
                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                        {formik.errors.contractor}
                                    </Typography>
                                )}

                                {/* Attachments Section */}
                                <Box
                                    sx={{
                                        mt: 2,
                                        border: "1px solid #ECECEC",
                                        borderRadius: 1,
                                        p: 2,
                                        backgroundColor: "#FFFFFF",
                                        width: "100%",
                                        boxSizing: "border-box",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            mb: 1,
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <FaPaperclip color="#5B5772" />
                                            <Typography
                                                sx={{
                                                    ml: 1,
                                                    fontWeight: 500,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    color: "#433F5E",
                                                    fontFamily: "Outfit, sans-serif",
                                                }}>
                                                Attachments
                                            </Typography>
                                        </Box>

                                        {files.length > 0 && (
                                            <Box
                                                onClick={() => document.getElementById("fileUploadInput").click()}
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    cursor: "pointer",
                                                    gap: "4px",
                                                }}
                                            >
                                                <Typography

                                                    sx={{
                                                        fontSize: "14px",
                                                        color: "#433F5E",
                                                        fontWeight: 400,
                                                        lineHeight: "100%",
                                                    }}
                                                >
                                                    Add more
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        backgroundColor: "#ECECEC", // green
                                                        borderRadius: "50%",
                                                        width: 20,
                                                        height: 20,
                                                    }}
                                                >
                                                    <IoAdd size={14} color="#140F36" />
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Upload Box */}
                                    {files.length === 0 && (
                                        <Box
                                            sx={{
                                                mt: 1,
                                                position: "relative",
                                                borderRadius: "3px",
                                                backgroundColor: "#ECECEC",
                                                height: 100,
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                cursor: "pointer",
                                                transition: "all 0.2s ease",
                                                backgroundImage: `
                                                    repeating-linear-gradient(90deg, rgba(113,131,155,0.7) 0 5px, transparent 5px 10px),   /* top */
                                                    repeating-linear-gradient(180deg, rgba(113,131,155,0.7) 0 5px, transparent 5px 10px), /* right */
                                                    repeating-linear-gradient(90deg, rgba(113,131,155,0.7) 0 5px, transparent 5px 10px),  /* bottom */
                                                    repeating-linear-gradient(180deg, rgba(113,131,155,0.7) 0 5px, transparent 5px 10px)  /* left */
                                                    `,
                                                backgroundRepeat: "no-repeat",
                                                backgroundSize: "100% 1px, 1px 100%, 100% 1px, 1px 100%",
                                                backgroundPosition: "top left, top right, bottom left, top left",
                                                // "&:hover": {
                                                //     backgroundColor: "#F0EEF6",
                                                //     borderColor: "#8A7FD5",
                                                // },
                                            }}
                                            onClick={() => document.getElementById("fileUploadInput").click()}
                                        >
                                            <input
                                                type="file"
                                                id="fileUploadInput"
                                                hidden
                                                multiple
                                                //onChange={(e) => console.log(e.target.files)}
                                                onChange={handleFileUpload}
                                            />
                                            <Box
                                                sx={{
                                                    position: "relative",
                                                    zIndex: 1,
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    color: "#6B5FA4",
                                                }}
                                            >
                                                <FiUpload color="#71839B" size={42} />
                                                <Typography
                                                    sx={{
                                                        mt: 1,
                                                        fontSize: "14px",
                                                        fontWeight: 400,
                                                        lineHeight: "100%",
                                                        letterSpacing: "0.14px",
                                                        color: "#71839B",
                                                        fontFamily: "Outfit, sans-serif",
                                                    }}
                                                >
                                                    Upload attachment
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}

                                    {/* Uploaded Files List */}
                                    {files.length > 0 && (
                                        <Box
                                            sx={{
                                                mt: 1,
                                                maxHeight: files.length > 1 ? 145 : "none",
                                                overflowY: files.length > 1 ? "auto" : "visible",
                                                pr: files.length > 1 ? 1 : 0,
                                                "&::-webkit-scrollbar": {
                                                    width: "6px",
                                                },
                                                "&::-webkit-scrollbar-thumb": {
                                                    backgroundColor: "#C1C1C1",
                                                    borderRadius: "3px",
                                                },
                                            }}
                                        >

                                            <input
                                                type="file"
                                                id="fileUploadInput"
                                                hidden
                                                multiple
                                                onChange={handleFileUpload}
                                            />

                                            {files.map((file) => (
                                                <Box
                                                    key={file.id}
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "flex-start",
                                                        justifyContent: "space-between",
                                                        p: 1,
                                                        mt: 0.2,
                                                        //borderBottom: "1px solid #E0E0E0",
                                                    }}
                                                >
                                                    {/* File info */}
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        {/* <Box
                                                                    sx={{
                                                                        width: 24,
                                                                        height: 28,
                                                                        borderRadius: "1px",
                                                                        backgroundColor: "#E9F0FA",
                                                                        display: "flex",
                                                                        justifyContent: "center",
                                                                        alignItems: "center",
                                                                        fontWeight: 600,
                                                                        color: "#FFFFFF",
                                                                        fontSize: "8px",
                                                                        lineHeight: "100%",
                                                                    }}
                                                                >
                                                                    {file.type}
                                                                </Box> */}
                                                        <Box
                                                            sx={{
                                                                position: "relative",
                                                                width: 24,
                                                                height: 28,
                                                                borderRadius: "1px",
                                                                backgroundColor: "#E9F0FA",
                                                                display: "flex",
                                                                justifyContent: "center",
                                                                alignItems: "flex-end",
                                                                fontWeight: 600,
                                                                fontSize: "8px",
                                                                color: "#FFFFFF",
                                                                textTransform: "uppercase",
                                                                fontFamily: "Outfit, sans-serif",
                                                                overflow: "hidden",
                                                                "&::before": {
                                                                    content: '""',
                                                                    position: "absolute",
                                                                    top: 0,
                                                                    right: 0,
                                                                    borderTop: "10px solid #D0D5DD",
                                                                    borderLeft: "10px solid transparent",
                                                                    borderTopRightRadius: "4px",
                                                                },
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    position: "absolute",
                                                                    bottom: 4,
                                                                    width: "100%",
                                                                    borderRadius: "1px",
                                                                    backgroundColor:
                                                                        file.type === "PDF"
                                                                            ? "#F14848"
                                                                            : file.type === "PNG"
                                                                                ? "#4881F1"
                                                                                : file.type === "JPG"
                                                                                    ? "#6A1B9A"
                                                                                    : file.type === "GIF"
                                                                                        ? "#F17048"
                                                                                        : "#546E7A",
                                                                    height: "13px",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                }}
                                                            >
                                                                {file.type}
                                                            </Box>
                                                        </Box>
                                                        <Box>
                                                            <Typography
                                                                sx={{
                                                                    fontWeight: 500,
                                                                    fontSize: "14px",
                                                                    color: "#324054",
                                                                    lineHeight: "22px",
                                                                    fontFamily: "Outfit, sans-serif",
                                                                }}
                                                            >
                                                                File {file.name}
                                                            </Typography>

                                                            {/* File size or error */}
                                                            {file.error ? (
                                                                <Typography sx={{ fontWeight: 400, fontSize: "12px", color: "#F92828", lineHeight: "16px", letterSpacing: "-0.06px" }}>
                                                                    {file.error}
                                                                </Typography>
                                                            ) : (
                                                                <Typography
                                                                    sx={{
                                                                        fontWeight: 400,
                                                                        fontSize: "12px",
                                                                        color: "#71839B",
                                                                        lineHeight: "16px",
                                                                        fontFamily: "Outfit, sans-serif",
                                                                        letterSpacing: "-0.06px",
                                                                    }}
                                                                >
                                                                    {file.size}. {file.date}
                                                                </Typography>
                                                            )}

                                                            {/* Upload progress */}
                                                            {!file.error && file.status === "uploading" && (
                                                                <Box sx={{ mt: 1 }}>
                                                                    <LinearProgress
                                                                        variant="determinate"
                                                                        value={file.progress}
                                                                        sx={{
                                                                            height: 6,
                                                                            width: 200,
                                                                            borderRadius: "4px",
                                                                            backgroundColor: "#E4ECF5",
                                                                            "& .MuiLinearProgress-bar": {
                                                                                backgroundColor: "#00B65E",
                                                                            },
                                                                        }}
                                                                    />
                                                                    <Typography
                                                                        sx={{
                                                                            fontWeight: 400,
                                                                            fontSize: "12px",
                                                                            color: "#71839B",
                                                                            lineHeight: "16px",
                                                                            fontFamily: "Outfit, sans-serif",
                                                                            letterSpacing: "-0.06px",
                                                                            mt: 0.5,
                                                                            textAlign: "left",
                                                                        }}
                                                                    >
                                                                        {file.progress}% Completed
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Box>

                                                    {/* Icons */}
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: "0.1px" }}>

                                                        {file.status === "uploading" && (
                                                            <IconButton onClick={() => handleRemoveFile(file.id)}>
                                                                <LuX color="#71839B" size={15} />
                                                            </IconButton>
                                                        )}

                                                        {file.status === "uploaded" && !file.error && (
                                                            <>
                                                                <IconButton>
                                                                    <LuDownload color="#71839B" size={15} />
                                                                </IconButton>
                                                                <IconButton
                                                                    onClick={() => handleRemoveFile(file.id)}
                                                                >
                                                                    <LuTrash2 color="red" size={15} />
                                                                </IconButton>
                                                            </>
                                                        )}
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}

                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-start",
                                        alignItems: "center",
                                        gap: 1,
                                        flexWrap: "wrap",
                                        mt: files.length > 1 ? 1 : 7,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            flexGrow: 1,
                                            color: "#726F86",
                                            fontWeight: 400,
                                            fontSize: "14px",
                                            lineHeight: "100%",
                                            fontFamily: "Outfit, sans-serif",
                                        }}
                                    >
                                        Please review info
                                    </Typography>
                                    <Button onClick={handleClose}
                                        sx={{
                                            textTransform: "none",
                                            color: "#F15744",
                                            fontWeight: 500,
                                            fontSize: "14px",
                                            lineHeight: "100%",
                                            fontFamily: "Outfit, sans-serif",
                                        }}>
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        sx={{
                                            backgroundColor: "#00344C",
                                            borderRadius: "25px",
                                            textTransform: "none",
                                            fontWeight: 500,
                                            fontSize: "14px",
                                            lineHeight: "100%",
                                            fontFamily: "Outfit, sans-serif",
                                            color: "#FFFFFF",
                                            px: "14px",
                                            py: "8px",
                                        }}
                                    >
                                        Save issue
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </DialogContent>
                </form>
            </Dialog>
        </>
    );
};

export default EditSafety;